import type { FC } from 'react';
import { useCallback, useDeferredValue, useEffect, useState } from 'react';

import { Button, Col, Flex, Row, Select, Tooltip, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconAll from '@/assets/icons/icon-all.svg?react';
import IconPlus from '@/assets/icons/icon-plus.svg?react';
import Card from '@/components/RCard';
import PageHeader from '@/components/RPageHeader';
import SearchInput from '@/components/RSearchInput';
import UpdateTime from '@/components/RUpdateTime';
import { RUsageLimitIndicator } from '@/components/RUsageLimitIndicator';
import useAuth from '@/hooks/useAuth';
import useGenerationTypes from '@/hooks/useGenerationTypes';
import usePlantsTableColumns from '@/hooks/usePlantsTableColumns';
import useProductTour from '@/hooks/useProductTour';
import {
  GenerationTypeKey,
  PlantsResponse,
  QueryParamsFilterProps,
} from '@/types/plant';
import { createQueryString, hasNonEmptyValues } from '@/utils';

import PlantCreateDrawer from './Drawer/PlantCreateDrawer';
import PlantGenericDrawer from './Drawer/PlantGenericDrawer';
import { DrawerType } from './Drawer/types';
import StyledPlants from './styles';

const Plants: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const generationTypes = useGenerationTypes();
  const [selectedGenerationType, setSelectedGenerationType] = useState<
    GenerationTypeKey | 'all'
  >('all');
  const [nameSearchValue, setNameSearchValue] = useState<string>();
  const [selectedActiveStatus, setSelectedActiveStatus] = useState<
    'all' | 'active' | 'inactive'
  >('active');

  const deferredNameSearch = useDeferredValue(nameSearchValue);
  const [queryParams, setQueryParams] = useState<QueryParamsFilterProps>({
    name: '',
    generationType: undefined,
    isActive: undefined,
  });

  const [pageSize, setPageSize] = useState<number>(7);
  const [offset, setOffset] = useState(1);

  const queryString = createQueryString(queryParams);
  const plantsCreateRef = useProductTour('createPlant');

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      name: deferredNameSearch || '',
      generationType: selectedGenerationType,
      isActive: selectedActiveStatus,
    }));
  }, [deferredNameSearch, selectedGenerationType, selectedActiveStatus]);

  const {
    data: plantsData,
    isLoading,
    mutate,
  } = useSWR<PlantsResponse>(
    hasNonEmptyValues(queryParams)
      ? `/plant/?limit=${pageSize}&offset=${
          (offset - 1) * pageSize
        }&${queryString}`
      : null
  );

  const assetLimit = user?.organization.subscription.plan.simAssetLimit;
  const [drawer, setDrawer] = useState<DrawerType>({
    type: null,
    data: null,
  });
  const updateDrawer = useCallback(
    (type: DrawerType['type'], data: DrawerType['data']) => {
      setDrawer({ type, data });
    },
    []
  );

  const [isCreatePlantDrawerOpen, setIsCreatePlantDrawerOpen] =
    useState<boolean>(false);

  const plantsTableColumns = usePlantsTableColumns(mutate, updateDrawer);

  const handlePaginationChange = (page: number) => {
    setOffset(page);
  };

  const filterButtons = [
    {
      label: t('plants.filterButtons.all'),
      value: 'all',
      icon: <IconAll fill="white" />,
    },
    ...Object.values(generationTypes),
  ];

  const handleCreateDrawerOpen = () => {
    setIsCreatePlantDrawerOpen(true);
  };

  return (
    <StyledPlants span={24}>
      <PageHeader
        title={t('plants.pageTitle')}
        description={t('plants.pageDescription')}
        rightSideSection={<RUsageLimitIndicator.Plants />}
      />
      <Card title={t('plants.allPlants.title')}>
        <Row
          justify="space-between"
          className="filter-header"
          style={{ padding: '22px' }}
        >
          <Col className="left-side">
            <Row>
              <Col>
                <Row
                  align="middle"
                  justify="space-between"
                  gutter={[4, 4]}
                  style={{
                    minWidth: '160px',
                    padding: '4px 2px',
                    border: '1px solid',
                    height: '36px',
                    borderColor: theme.colors.grayLightAccent,
                    borderRadius: theme.main.borderRadius,
                  }}
                >
                  {filterButtons.map((item) => (
                    <Tooltip
                      key={item.value}
                      trigger="hover"
                      title={item.label}
                      color={theme.colors.grayDarkPrimary}
                    >
                      <Col
                        flex={'28px'}
                        key={item.value}
                        style={{
                          height: '100%',
                        }}
                      >
                        <Button
                          className={
                            selectedGenerationType === item.value
                              ? 'selected-filter-buttons-styles'
                              : 'filter-buttons-styles'
                          }
                          onClick={() =>
                            setSelectedGenerationType(
                              item.value as GenerationTypeKey
                            )
                          }
                          type={
                            selectedGenerationType === item.value
                              ? 'primary'
                              : 'link'
                          }
                          icon={item.icon}
                        />
                      </Col>
                    </Tooltip>
                  ))}
                </Row>
              </Col>
              <Col className="pl-16">
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <SearchInput
                    placeholder={t('plants.allPlants.searchPlaceholder')}
                    onChange={(e) => setNameSearchValue(e.target.value)}
                  />
                </Row>
              </Col>
              <Col className="pl-16">
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <Select
                    defaultValue={selectedActiveStatus}
                    placeholder={t('plants.allPlants.statusPlaceholder')}
                    style={{ width: 180 }}
                    onChange={(val) =>
                      setSelectedActiveStatus(
                        val as 'all' | 'active' | 'inactive'
                      )
                    }
                    options={[
                      {
                        value: 'all',
                        label: t('plants.allPlants.statusOptions.all'),
                      },
                      {
                        value: 'active',
                        label: t('plants.allPlants.statusOptions.active'),
                      },
                      {
                        value: 'inactive',
                        label: t('plants.allPlants.statusOptions.inactive'),
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col className="pl-16">
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <UpdateTime fetchFunction={mutate} />
                </Row>
              </Col>
            </Row>
          </Col>
          <Flex align="center" ref={plantsCreateRef}>
            <Button
              type="primary"
              icon={<IconPlus fill={theme.colors.light} />}
              onClick={() => handleCreateDrawerOpen()}
              data-testid="create-plant-btn"
            >
              {t('plants.allPlants.create')}
            </Button>
          </Flex>
        </Row>
        <Table
          rowKey="id"
          columns={plantsTableColumns}
          dataSource={plantsData?.results}
          loading={isLoading}
          size="small"
          pagination={{
            total: plantsData?.count,
            pageSizeOptions: ['7', '10', '20', '50', '100'],
            pageSize: pageSize,
            hideOnSinglePage: true,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            onShowSizeChange: (current, size) => {
              setOffset(current);
              setPageSize(size);
            },
            showSizeChanger: true,
          }}
          scroll={{ x: 1120 }}
        />
      </Card>
      {assetLimit && plantsData && isCreatePlantDrawerOpen && (
        <PlantCreateDrawer
          mutate={mutate}
          open={isCreatePlantDrawerOpen}
          setIsOpen={setIsCreatePlantDrawerOpen}
          maxDataCount={assetLimit}
          currentDataCount={plantsData?.count}
        />
      )}
      {assetLimit && plantsData && drawer.type && (
        <PlantGenericDrawer
          mutate={mutate}
          drawerOptions={drawer}
          setDrawerOptions={setDrawer}
          maxDataCount={assetLimit}
          currentDataCount={plantsData?.count}
        />
      )}
    </StyledPlants>
  );
};

export default Plants;
